/**
 * FlexNet Part Number gate types and display helpers (SITMP-7497).
 *
 * Gate evaluation lives in containment-service
 * {@code POST /mcc/containments/flexnet/part-number-gate} (threshold 7.1.0;
 * null/blank FlexNet version fails the gate).
 */

/** Must stay aligned with containment-service FlexNetVersionSupport. */
export const MinFlexnetVersionForPnRemoval = "7.1.0";

export type PlantWithFlexNetVersion = {
	id?: string;
	name?: string;
	code?: string;
	/** Preferred client-side field name. */
	flexNetVersion?: string | null;
	/** Jackson camelCase from containment-service PlantData.flexnetVersion. */
	flexnetVersion?: string | null;
};

export type UnsupportedFlexNetPlant = {
	id?: string;
	name: string;
	/** On-record version for hover; null/blank when the API did not supply one. */
	flexNetVersion: string | null;
};

export type PartNumberGateResult = {
	/** True when at least one selected plant fails the server-side gate. */
	partNumberRequired: boolean;
	unsupportedPlants: UnsupportedFlexNetPlant[];
};

/** Payload plant for {@code POST /containments/flexnet/part-number-gate}. */
export type FlexNetPartNumberGatePlantPayload = {
	id?: string;
	name?: string;
	code?: string;
	flexnetVersion?: string | null;
};

/** Prefer API flexnetVersion; preserve null/blank for the server gate. */
export function ToFlexNetPartNumberGatePayload(
	plant: PlantWithFlexNetVersion
): FlexNetPartNumberGatePlantPayload {
	const raw = plant.flexnetVersion ?? plant.flexNetVersion;
	const trimmed =
		raw == null ? null : String(raw).trim() === "" ? null : String(raw).trim();
	return {
		id: plant.id,
		name: plant.name,
		code: plant.code,
		flexnetVersion: trimmed,
	};
}

/** Map server PlantData rows to warning-card shape. */
export function ToUnsupportedFlexNetPlants(
	plants: Array<{
		id?: string;
		name?: string;
		code?: string;
		flexnetVersion?: string | null;
		flexNetVersion?: string | null;
	}> | null | undefined
): UnsupportedFlexNetPlant[] {
	return (plants ?? []).map((plant) => {
		const version = plant.flexnetVersion ?? plant.flexNetVersion;
		return {
			id: plant.id,
			name: plant.name?.trim() || plant.code?.trim() || plant.id || "Unknown plant",
			flexNetVersion:
				version == null || String(version).trim() === ""
					? null
					: String(version).trim(),
		};
	});
}
